// camSets — the SHARED camera-set derivation for the video grid (#86bb7phhh).
//
// Live and playback show the SAME grid: the cameras for the current SET (bank). This is the ONE place
// that decides "which cameras" — mirroring iOS's single CameraConfModel.getConfCameras(conf, setIndex),
// so Live and playback can't drift. Only the controls + tile content (live stream vs recorded) differ.
//
// `currentCamView` is the set encoding used app-wide (state.camera.currentCamView), e.g. 'cam_1-9' or the
// single-cam 'cam_5'. `cameras` is state.server.cameras, which ViewActions.selectView has already
// REORDERED into the current view's arrangement — so slicing it yields the view's cameras in view order.

/**
 * getVisibleCameras — the cameras shown for the current set/bank.
 *   'cam_1-9'  -> cameras.slice(0, 9)   (a 9-up page)
 *   'cam_10-16'-> cameras.slice(9, 16)
 *   'cam_5'    -> cameras.slice(4, 5)   (single cam)
 * Returns [] for an empty/absent camera list.
 *
 * @param {Array}  cameras        state.server.cameras (view-arranged)
 * @param {string} currentCamView the set encoding (state.camera.currentCamView)
 * @returns {Array} the sliced cameras (raw entries; each viewer adapts to its own tile shape)
 */
export const getVisibleCameras = (cameras, currentCamView) => {
  if (!Array.isArray(cameras) || cameras.length === 0) return [];
  const parts = String(currentCamView || 'cam_1').replace('cam_', '').split('-');
  const start = (parseInt(parts[0], 10) || 1) - 1;
  const end = parts.length > 1 ? (parseInt(parts[1], 10) || start + 1) : start + 1;
  return cameras.slice(Math.max(0, start), Math.max(0, end));
};

// The state.server key holding the SET (bank) definitions for each conf. conf-1's cameraArr entries are
// [ 'cam_N', bID, enabled ]; the multi-cam *View entries are [ 'A-B', enabled ] (A-B = 1-based range).
const SET_ARRAY_KEY = {
  'conf-1': 'cameraArr', 'conf-2': 'twoView', 'conf-4': 'quadView', 'conf-6': 'sixView',
  'conf-8': 'eightView', 'conf-9': 'nineView', 'conf-12': 'twelveView', 'conf-16': 'sixteenView',
};

/**
 * enabledSetCamViews — the ordered `currentCamView` strings for the ENABLED sets of a conf, read from the
 * SAME state.server arrays CamButtonsView renders. Empty/disabled banks are dropped (a view rarely fills
 * every bank), so the strip and the swipe both page only REAL sets — including non-contiguous ones (bank 1
 * has cams, bank 2 empty, bank 3 has cams → [bank1, bank3]). David 2026-08-04: only show sets we can
 * actually fill. #86bb7phhh.
 *
 * @param {object} server state.server (holds cameraArr / twoView / … with [range, enabled] entries)
 * @param {string} conf   'conf-N'
 * @returns {string[]} e.g. ['cam_1-9', 'cam_16-24', 'cam_24-32']
 */
export const enabledSetCamViews = (server, conf) => {
  const arr = (server && server[SET_ARRAY_KEY[conf]]) || [];
  if (conf === 'conf-1') return arr.filter((c) => c && c[2]).map((c) => c[0]); // c[0] is already 'cam_N'
  return arr.filter((v) => v && v[1]).map((v) => `cam_${v[0]}`);
};

/**
 * swapVisibleInFull — swap two VISIBLE-set slots within the FULL shared arrangement (state.server.cameras).
 * The playback/Live grid shows `cameras.slice(start, end)` for the current set; a swap of visible slots
 * `from`/`to` maps to full indices `start+from` / `start+to` (start = the set offset from currentCamView),
 * so a rearrange edits the ONE shared array — a real, dirty-able, saveable view change (#86bb7phhh).
 * Returns a NEW reordered array on a real swap, or the SAME reference on a no-op (out of range / from===to),
 * so callers can skip a needless dispatch (which would falsely mark the view dirty).
 *
 * @param {Array}  cameras        state.server.cameras (view-arranged, full)
 * @param {string} currentCamView the set encoding (e.g. 'cam_3-4')
 * @param {number} from           visible-set slot index
 * @param {number} to             visible-set slot index
 * @returns {Array} reordered full array (new ref), or `cameras` unchanged on a no-op
 */
export const swapVisibleInFull = (cameras, currentCamView, from, to) => {
  const full = Array.isArray(cameras) ? cameras : [];
  const parts = String(currentCamView || 'cam_1').replace('cam_', '').split('-');
  const start = (parseInt(parts[0], 10) || 1) - 1;
  const fi = start + from;
  const ti = start + to;
  if (fi < 0 || ti < 0 || fi >= full.length || ti >= full.length || fi === ti) return full; // no-op → same ref
  const next = full.slice();
  const tmp = next[fi]; next[fi] = next[ti]; next[ti] = tmp;
  return next;
};

/**
 * assignVisibleInFull — place `cam` at a VISIBLE-set slot within the FULL shared arrangement
 * (state.server.cameras), so adding a camera in playback is a REAL, dirty-able, SAVEABLE view edit —
 * the same shared-array contract as swapVisibleInFull (#86bb7phhh, David 2026-08-13). Maps the visible
 * slot to full index `start + slot` (start = the set offset from currentCamView); replaces in range,
 * or pads with fEnable:false empty markers up to the index and places there (fill-past-the-end case).
 * Returns a NEW array (or the same ref on a no-op: no cam).
 *
 * @param {Array}  cameras        state.server.cameras (view-arranged, full)
 * @param {string} currentCamView the set encoding (e.g. 'cam_1-9')
 * @param {number} slot           visible-set slot index
 * @param {object} cam            normalized slot camera (normalizeSlotCamera output)
 * @returns {Array} new full array, or `cameras` unchanged on a no-op
 */
export const assignVisibleInFull = (cameras, currentCamView, slot, cam) => {
  const full = Array.isArray(cameras) ? cameras.slice() : [];
  if (!cam) return Array.isArray(cameras) ? cameras : full; // no-op → same ref
  const parts = String(currentCamView || 'cam_1').replace('cam_', '').split('-');
  const start = (parseInt(parts[0], 10) || 1) - 1;
  const idx = Math.max(0, start + slot);
  while (full.length < idx) full.push({ fEnable: false }); // pad empties so idx is addressable
  full[idx] = cam;
  return full;
};

/**
 * removeVisibleInFull — remove the cam at a VISIBLE-set slot from the FULL shared arrangement,
 * COMPACTING (splice out) so the grid re-flows — mirrors the local removeAndResize, and makes a
 * removal a real dirty-able/saveable view edit (#86bb7phhh, David 2026-08-13). Returns a NEW array,
 * or the same ref on a no-op (out of range).
 *
 * @param {Array}  cameras        state.server.cameras (view-arranged, full)
 * @param {string} currentCamView the set encoding
 * @param {number} slot           visible-set slot index
 * @returns {Array} new (compacted) full array, or `cameras` unchanged on a no-op
 */
export const removeVisibleInFull = (cameras, currentCamView, slot) => {
  const full = Array.isArray(cameras) ? cameras : [];
  const parts = String(currentCamView || 'cam_1').replace('cam_', '').split('-');
  const start = (parseInt(parts[0], 10) || 1) - 1;
  const idx = start + slot;
  if (idx < 0 || idx >= full.length) return full; // no-op → same ref
  const next = full.slice();
  next.splice(idx, 1);
  return next;
};

export default getVisibleCameras;
